/* eslint-disable max-lines */
import React from 'react';
import { getMetricsFormula } from '@shared/constants/dashboard/campaign-table-constants';
import { formatValueToCell, getTotalColumn, NAME } from '@shared/tables';
import { slugIsCampaignType } from '@domain/segments/segments.utils';
import { SelectedGroupWithSegments } from '@domain/segments/segments.types';
import { ObjectSelected } from '@domain/object-selected';
import { NoTranslate } from '@shared/components/no-translate/no-translate';
import {
    ClusterSwitch,
    MonthlyBudgetEdit,
} from '@domain/dashboard/cluster/campaign-table-cluster-components';
import tagsGroupConstants from '@shared/constants/overview/TagsGroupConstants';
import { ClusterNameWrapper } from '@domain/segments/segments-table/cluster-name-wrapper/cluster-name-wrapper';
import { METRICS_TYPES } from '@shared/constants/MetricsTypeConstants';
import { formatValuesForDisplayBasedOnType } from '@shared/utils/strings';
import { PaceBudgetEdit } from '@domain/dashboard/cluster/campaign-table-cluster-components/pace-budget-edit';
import { MinimumPerformanceValueEdit } from '@domain/dashboard/cluster/campaign-table-cluster-components/minimum-performance-value-edit';
import { PerformanceWindowEdit } from '@domain/dashboard/cluster/campaign-table-cluster-components/performance-window-edit';
import { PaceTargetEdit } from '@domain/dashboard/cluster/campaign-table-cluster-components/pace-target-edit';
import {
    DaysBetweenAdjustmentsTargetEdit,
} from '@domain/dashboard/cluster/campaign-table-cluster-components/days-between-adjustments-target-edit';
import {
    DaysBetweenAdjustmentsBudgetEdit,
} from '@domain/dashboard/cluster/campaign-table-cluster-components/days-between-adjustments-budget-edit';
import { MinimumDailyBudgetValueEdit } from '@domain/dashboard/cluster/campaign-table-cluster-components/minimum-daily-budget-value-edit';
import { SegmentsMonthlyBudgetTotalLabel, SegmentsTotalLabel } from './segments-table.styles';
import { METRICS_TO_CONSOLIDATE, UNTAGGED, MULTIPLE_STRATEGIES } from './segments-table.constants';

export const getSegmentsColumnProps = ({
    default: {
        totalHeader: ({ column, rows }) => getTotalColumn(column, rows),
        CellComponent: ({ value, column }) => formatValueToCell(value, column),
    },
    [NAME]: {
        totalHeader: () => (<SegmentsTotalLabel>Total</SegmentsTotalLabel>),
        CellComponent: ({ value, column }: any) => (
            <NoTranslate>
                {formatValueToCell(value, column)}
            </NoTranslate>
        ),
    },
    cluster_name: {
        totalHeader: () => ('Total'),
        CellComponent: props => <ClusterNameWrapper {...props} />,
    },
    status_bid_manager: {
        totalHeader: () => (''),
        CellComponent: props => <ClusterSwitch {...props} isBidManager />,
    },
    status_budget_manager: {
        totalHeader: () => (''),
        CellComponent: props => <ClusterSwitch {...props} isBudgetManager />,
    },
    monthly_budget: {
        totalHeader: (props: any) => {
            const { data } = props;
            const totalMonthlyBudget = data.reduce(
                (a, b) => (a + parseFloat(b.monthly_budget || 0)), 0.0,
            );

            return (
                <SegmentsMonthlyBudgetTotalLabel>
                    {
                        totalMonthlyBudget === 0.0
                            ? '0.00' : formatValuesForDisplayBasedOnType(totalMonthlyBudget, METRICS_TYPES.integer)
                    }
                </SegmentsMonthlyBudgetTotalLabel>
            );
        },
        CellComponent: props => (<MonthlyBudgetEdit {...props} />),
    },
    pace_budget: {
        totalHeader: () => (''),
        CellComponent: props => <PaceBudgetEdit {...props} />,
    },
    pace_target: {
        totalHeader: () => (''),
        CellComponent: props => <PaceTargetEdit {...props} />,
    },
    minimum_performance_value: {
        totalHeader: () => (''),
        CellComponent: props => <MinimumPerformanceValueEdit {...props} />,
    },
    performance_window: {
        totalHeader: () => (''),
        CellComponent: props => <PerformanceWindowEdit {...props} />,
    },
    days_between_adjustments_target: {
        totalHeader: () => (''),
        CellComponent: props => <DaysBetweenAdjustmentsTargetEdit {...props} />,
    },
    days_between_adjustments_budget: {
        totalHeader: () => (''),
        CellComponent: props => <DaysBetweenAdjustmentsBudgetEdit {...props} />,
    },
    minimum_daily_budget_value: {
        totalHeader: () => (''),
        CellComponent: props => <MinimumDailyBudgetValueEdit {...props} />,
    },
});

export const formatSegmentsToColumns = segmentsMetrics => segmentsMetrics.map((metric) => {
    const { value: metricValue } = metric;
    const columnsProps = getSegmentsColumnProps;

    const headerProps = columnsProps[metricValue]
        ? columnsProps[metricValue]
        : columnsProps.default;

    const cellProps = columnsProps[metricValue]
        ? columnsProps[metricValue]
        : columnsProps.default;

    return {
        Header: metric.label,
        accessor: metric.value,
        Cell: cellProps.CellComponent,
        Total: headerProps.totalHeader,
        metric,
    };
});

const isValidCampaign = (campaign, campaigns) => campaign?.['campaign_id'] in campaigns;

const shouldInsertTag = (campaign, campaigns) => isValidCampaign(campaign, campaigns);

export const getCampaignIdsByTags = (
    selectedGroup: SelectedGroupWithSegments = {} as SelectedGroupWithSegments,
    campaigns,
    objectSelected: ObjectSelected,
) => {
    if (slugIsCampaignType(selectedGroup)) {
        return getTagsWithCampaignsIdsWhenIsCampaignType(selectedGroup, campaigns);
    }

    const { taggedCampaigns = {} } = selectedGroup;
    const formattedTaggedCampaigns = objectSelected.isAccountSelected
        ? filterTaggedCampaignsByPlatform(
            objectSelected?.['account_selected']?.['content_type_name'],
            Object.values(taggedCampaigns),
        )
        : Object.values(taggedCampaigns);

    const campaignsThatShouldTagged = formattedTaggedCampaigns
        .filter(campaign => shouldInsertTag(campaign, campaigns));

    const tagsWithCampaignIds = campaignsThatShouldTagged.reduce((acc, campaign) => {
        const [tag] = campaign.tags;
        const campaignsIds = tag in acc ? acc[tag] : [];
        return {
            ...acc,
            [tag]: [...campaignsIds, campaign.campaign_id],
        };
    }, {});

    const filteredCampaignIdsUntagged = getUntaggedCampaigns(taggedCampaigns, campaigns);

    return {
        ...tagsWithCampaignIds,
        [UNTAGGED]: filteredCampaignIdsUntagged,
    };
};

// TODO - https://dev.azure.com/paretoio/paretech/_sprints/taskboard/Squad%20CCR/paretech/squad-ccr/Q3.S2?workitem=16546
// Fazer refatoração com middlaware para o campaigntype
const getTagsWithCampaignsIdsWhenIsCampaignType = (
    selectedGroup: SelectedGroupWithSegments,
    campaigns = {},
) => {
    const formattedTaggedCampaigns = Object.values(campaigns);

    return formattedTaggedCampaigns.reduce((acc, campaign) => {
        const { type } = campaign;
        const campaignsIds = type in acc ? acc[type] : [];
        return {
            ...acc,
            [type]: [...campaignsIds, campaign.campaign_id],
        };
    }, {});
};

const filterTaggedCampaignsByPlatform = (
    platform,
    tags,
) => Object.values(tags).filter(
    tag => tag?.platform === platform,
);

const getUntaggedCampaigns = (tagsWithCampaignIds, campaigns) => {
    const campaignIds = Object.keys(tagsWithCampaignIds);

    return Object.keys(campaigns).filter(campaignId => !campaignIds.includes(campaignId));
};

const getAllClusteredCampaigns = clusters => clusters.map(
    cluster => getCampaignsIdsFromCluster(cluster),
).flat();

const getCampaignsIdsFromCluster = cluster => cluster.clusterObjects.map(object => object.objectId);

export const getUnclusteredCampaigns = (clusters, campaigns) => Object.keys(campaigns).filter(
    campaignId => !getAllClusteredCampaigns(clusters).includes(campaignId),
);

export const aggregateCampaignDataByTag = (campaignIdsByTag, campaigns) => {
    const tagList = Object.keys(campaignIdsByTag);

    return tagList.reduce((acc, tag) => ({
        ...acc,
        [tag]: {
            ...consolidateMetrics(
                campaignIdsByTag[tag],
                campaigns,
            ),
            name: tag,
            bidStrategy: MULTIPLE_STRATEGIES,
        },
    }), {});
};

const consolidateBidStrategies = (
    campaignIdsOfTag: string[],
    allCampaigns: Record<string, any>,
) => {
    try {
        const campaignsOfTag = campaignIdsOfTag.map(campaignId => allCampaigns[campaignId]);
        const firstBidStrategy = campaignsOfTag[0].bid_strategy_type;

        campaignsOfTag.forEach((campaign) => {
            if (campaign.bid_strategy_type !== firstBidStrategy) {
                throw new Error('Different bid strategy found, setting multiple strategies.');
            }
        });

        return firstBidStrategy;
    } catch (_) {
        return MULTIPLE_STRATEGIES;
    }
};

export const aggregateClusteredCampaignData = (
    clusters, campaigns,
) => clusters.reduce((acc, cluster) => ({
    ...acc,
    [cluster.name]: {
        ...consolidateMetrics(
            getCampaignsIdsFromCluster(cluster),
            campaigns,
        ),
        name: cluster.name,
        cluster_name: cluster.name,
        status_budget_manager: cluster.isBudgetActive,
        status_bid_manager: cluster.isTargetActive,
        monthly_budget: cluster.ranges[0].budget,
        pace_budget: cluster?.paceBudget,
        pace_target: cluster?.paceTarget,
        minimum_performance_value: cluster?.minimumPerformanceValue,
        minimum_daily_budget_value: cluster?.minimumDailyBudgetValue,
        performance_window: cluster?.performanceWindow,
        bidStrategy: consolidateBidStrategies(getCampaignsIdsFromCluster(cluster), campaigns),
    },
}), {});

export const aggregateUnclusteredCampaignData = (
    unclusteredCampaignsIds, campaigns,
) => unclusteredCampaignsIds.reduce(acc => ({
    ...acc,
    [tagsGroupConstants.UNTAGGED]: {
        ...consolidateMetrics(
            unclusteredCampaignsIds,
            campaigns,
        ),
        cluster_name: tagsGroupConstants.UNTAGGED,
        name: tagsGroupConstants.UNTAGGED,
        bidStrategy: consolidateBidStrategies(unclusteredCampaignsIds, campaigns),
    },
}), {});

export const aggregateCampaignDataByCluster = (campaignIdsByTag, campaigns, clusters) => {
    const tagList = Object.keys(campaignIdsByTag);
    return tagList.reduce((acc, tag) => {
        if (tag === tagsGroupConstants.UNTAGGED) {
            return {
                ...acc,
                [tag]: {
                    ...consolidateMetrics(
                        campaignIdsByTag[tag],
                        campaigns,
                    ),
                    cluster_name: tag,
                    name: tag,
                    bidStrategy: consolidateBidStrategies(campaignIdsByTag[tag], campaigns),
                },
            };
        }

        return ({
            ...acc,
            [tag]: {
                ...consolidateMetrics(
                    campaignIdsByTag[tag],
                    campaigns,
                ),
                name: tag === tagsGroupConstants.UNTAGGED ? tag : clusters[tag.toLowerCase()]?.name,
                cluster_name: tag === tagsGroupConstants.UNTAGGED
                    ? tag : clusters[tag.toLowerCase()]?.name,
                status_budget_manager: clusters[tag.toLowerCase()]?.['status_budget_manager'],
                status_bid_manager: clusters[tag.toLowerCase()]?.['status_bid_manager'],
                monthly_budget: clusters[tag.toLowerCase()]?.['monthly_budget'],
                bidStrategy: consolidateBidStrategies(campaignIdsByTag[tag], campaigns),
            },
        });
    }, {});
};

export const consolidateMetrics = (campaignIds, campaigns) => {
    const metricsAggregated = getSumMetrics(campaignIds, campaigns);
    const calculatedMetrics = getCalculatedMetrics(metricsAggregated);

    return {
        ...metricsAggregated,
        ...calculatedMetrics,
    };
};

export const sortMetricsColumnsByIndex = (metrics: any[]) => metrics.sort(
    (actual, next) => actual.index - next.index,
);

const getSumMetrics = (campaignIds, campaigns) => METRICS_TO_CONSOLIDATE
    .reduce((acc, metric) => ({
        ...acc,
        [metric]: campaignIds.reduce((metricsAccumulated, campaignId) => {
            const campaign = campaigns[campaignId];
            return shouldSumMetric(metric, campaign)
                ? sumMetrics(metricsAccumulated, campaign[metric]) : metricsAccumulated;
        }, 0),
    }), {});

const shouldSumMetric = (metric, campaign) => campaign && (metric in campaign);

const sumMetrics = (metricA, metricB) => parseFloat(metricA) + parseFloat(metricB);

const getCalculatedMetrics = metrics => Object.entries(getMetricsFormula())
    .reduce((acc, [key, metricFormula]) => {
        const { dividend, divider, multiplier } = metricFormula;
        return {
            ...acc,
            [key]: calculateMetricObtainableThruDivision(
                metrics[dividend],
                metrics[divider],
                multiplier,
            ),
        };
    }, {});

const calculateMetricObtainableThruDivision = (dividend, divider, multiplier) => {
    if (!divider || !dividend) return 0;

    return multiplier * dividend / divider;
};
